(function () {
  'use strict';

  // Two possible flows depending on the "scope" answer.
  var FLOW_ROOM = ['scope', 'room', 'ventilation'];
  var FLOW_WHOLE_HOME = ['scope', 'space', 'ventilation'];

  function SolutionFinder(rootEl) {
    this.root = rootEl;
    this.mode = rootEl.dataset.sfMode || 'full';
    this.locale = rootEl.dataset.sfLocale || 'en';
    this.webhookUrl = rootEl.dataset.sfWebhook || '';

    try {
      this.config = JSON.parse(this.root.querySelector('[data-sf-config]').textContent);
      this.strings = JSON.parse(this.root.querySelector('[data-sf-strings]').textContent);
    } catch (e) {
      console.error('[SolutionFinder] Failed to parse config', e);
      return;
    }

    this.state = {
      step: 'intro',
      history: [],
      flow: FLOW_WHOLE_HOME, // default until scope is chosen
      scope: '',
      room: '',
      building: '',
      size: null,
      ventilation: ''
    };

    this.recommendation = null;
    this.modal = this.root.querySelector('[data-sf-modal]');
    this.container = this.root.querySelector('.sf-container');
    this.contactIframe = this.root.querySelector('[data-sf-contact-iframe]');
    this.awaitingSubmit = false;

    var contactForm = this.modal && this.modal.querySelector('[data-sf-modal-step="form"] form');
    if (contactForm && this.contactIframe) {
      contactForm.setAttribute('target', 'sf-contact-target');
    }

    this.bindEvents();
    this.renderStep('intro');
    this.track('sf_loaded', { mode: this.mode, locale: this.locale });

    // In modal mode, the wizard sits inside the theme's own
    // <basic-modal>. That outer element's own close sequence (X
    // button, Escape, backdrop) is responsible for its own scroll
    // lock, and our internal state (container/results visibility)
    // is separate from it. If the outer close doesn't run cleanly
    // to completion for any reason, its scroll-lock can be left
    // stuck. Rather than depend on that always working, watch the
    // outer modal's 'open' attribute directly and force our own full
    // cleanup the moment it's removed — regardless of how the close
    // was triggered, or whether the outer modal's own cleanup fully
    // completed.
    if (this.mode === 'modal') {
      var outerModal = this.root.closest('basic-modal');
      if (outerModal && window.MutationObserver) {
        var self = this;
        var observer = new MutationObserver(function (mutations) {
          mutations.forEach(function (m) {
            if (m.attributeName === 'open' && !outerModal.hasAttribute('open')) {
              self.forceCleanup();
            }
          });
        });
        observer.observe(outerModal, { attributes: true, attributeFilter: ['open'] });
      }
    }
  }

  // Forcibly resets everything to a known-good state: unlocks body
  // scroll regardless of what mechanism locked it, and resets the
  // wizard's own internal view back to the step flow (not stuck
  // showing results) so a future reopen starts clean.
  SolutionFinder.prototype.forceCleanup = function () {
    document.body.style.overflow = '';
    if (this.modal) this.modal.hidden = true;
    if (this.container) this.container.hidden = false;
  };

  SolutionFinder.prototype.bindEvents = function () {
    var self = this;

    this.root.addEventListener('click', function (e) {
      var actionEl = e.target.closest('[data-sf-action]');
      if (!actionEl || !self.root.contains(actionEl)) return;
      self.handleAction(actionEl.dataset.sfAction, actionEl, e);
    });

    // Auto-advance on single-select clicks for scope, room, ventilation
    this.root.addEventListener('click', function (e) {
      var input = e.target.closest('input[data-sf-input][data-sf-autoadvance]');
      if (input) self.handleAutoAdvanceClick(input);
    });

    this.root.addEventListener('change', function (e) {
      var input = e.target.closest('[data-sf-input]');
      if (input) self.handleInputChange(input);

      var sizeInput = e.target.closest('[data-sf-size-input]');
      if (sizeInput) self.handleSizeChange(sizeInput);
    });

    this.root.addEventListener('input', function (e) {
      var sizeInput = e.target.closest('[data-sf-size-input]');
      if (sizeInput) self.handleSizeChange(sizeInput);
    });

    var form = this.root.querySelector('[data-sf-modal-step="form"] form');
    if (form) {
      form.addEventListener('submit', function (e) {
        self.handleFormSubmit(e, form);
      });
    }

    // The hidden iframe finishes loading once Shopify's response comes back
    // after a native form submission. Guarded by awaitingSubmit so this
    // doesn't fire on the iframe's own initial blank-page load.
    if (this.contactIframe) {
      this.contactIframe.addEventListener('load', function () {
        if (!self.awaitingSubmit) return;
        self.awaitingSubmit = false;
        self.track('sf_form_submitted', { rule: self.recommendation && self.recommendation.ruleId });
        self.openModalStep('success');
      });
    }

    if (this.modal) {
      this.modal.addEventListener('click', function (e) {
        if (e.target === self.modal) self.closeModal();
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.modal && !self.modal.hidden) {
        self.closeModal();
      }
    });
  };

  SolutionFinder.prototype.handleAction = function (action, el, e) {
    switch (action) {
      case 'start':
        this.goToStep('scope');
        break;
      case 'back':
        this.goBack();
        break;
      case 'next-from-scope':
        this.goToStep(this.state.scope === 'room' ? 'room' : 'space');
        break;
      case 'next-from-room':
        this.goToStep('ventilation');
        break;
      case 'next-from-space':
        this.goToStep('ventilation');
        break;
      case 'open-results':
        this.computeRecommendation();
        this.openModal('results');
        break;
      case 'open-expert-form':
        this.openModalStep('form');
        this.prepareForm();
        this.track('sf_expert_form_open', { rule: this.recommendation && this.recommendation.ruleId });
        break;
      case 'back-to-results':
        this.openModalStep('results');
        break;
      case 'close-modal':
        this.closeModal();
        break;
      case 'restart':
        this.closeModal();
        this.restart();
        break;
    }
  };

  // Ventilation is the only step that auto-advances immediately on click
  // (matches original behavior). Scope and room use a Continue button,
  // enabled via the 'change' handler below, for reliable keyboard access.
  SolutionFinder.prototype.handleAutoAdvanceClick = function (input) {
    if (input.name === 'ventilation' && this.state.step === 'ventilation'
        && input.value === this.state.ventilation) {
      this.computeRecommendation();
      this.openModal('results');
    }
  };

  SolutionFinder.prototype.handleInputChange = function (input) {
    var name = input.name;
    if (name === 'scope') {
      this.state.scope = input.value;
      this.state.flow = input.value === 'room' ? FLOW_ROOM : FLOW_WHOLE_HOME;
      this.setButtonEnabled('next-from-scope', true);
    } else if (name === 'room') {
      this.state.room = input.value;
      this.setButtonEnabled('next-from-room', true);
    } else if (name === 'building') {
      this.state.building = input.value;
      this.initSizeSlider();
      this.setButtonEnabled('next-from-space', true);
    } else if (name === 'ventilation') {
      this.state.ventilation = input.value;
      this.setButtonEnabled('open-results', true);
    }
  };

  SolutionFinder.prototype.handleSizeChange = function (input) {
    var val = parseInt(input.value, 10);
    this.state.size = val;
    var readout = this.root.querySelector('[data-sf-size-readout]');
    if (readout) {
      readout.textContent = this.format(this.strings.size_readout, { size: val });
    }
  };

  SolutionFinder.prototype.setButtonEnabled = function (actionName, enabled) {
    var btn = this.root.querySelector('[data-sf-action="' + actionName + '"]');
    if (btn) btn.disabled = !enabled;
  };

  SolutionFinder.prototype.initSizeSlider = function () {
    var range = this.config.size_ranges[this.state.building];
    if (!range) return;
    var input = this.root.querySelector('[data-sf-size-input]');
    var sizeContainer = this.root.querySelector('[data-sf-size]');
    if (!input) return;

    input.min = range.min;
    input.max = range.max;
    input.step = range.step || 10;
    input.value = range.default;
    this.state.size = range.default;

    this.root.querySelector('[data-sf-size-min]').textContent = range.min;
    this.root.querySelector('[data-sf-size-max]').textContent = range.max + '+';
    var readout = this.root.querySelector('[data-sf-size-readout]');
    if (readout) {
      readout.textContent = this.format(this.strings.size_readout, { size: range.default });
    }

    if (sizeContainer) sizeContainer.hidden = false;
  };

  SolutionFinder.prototype.goToStep = function (step, skipHistory) {
    if (!skipHistory) this.state.history.push(this.state.step);
    this.state.step = step;
    this.renderStep(step);
    this.track('sf_step', { step: step });
  };

  SolutionFinder.prototype.goBack = function () {
    var prev = this.state.history.pop();
    if (!prev) return;
    this.state.step = prev;
    this.renderStep(prev);
    this.track('sf_back', { to: prev });
  };

  SolutionFinder.prototype.renderStep = function (step) {
    var intro = this.root.querySelector('[data-sf-show-on="intro"]');
    var stepHeader = this.root.querySelector('[data-sf-show-on="step"]');

    if (intro) intro.hidden = step !== 'intro';
    if (stepHeader) stepHeader.hidden = (step === 'intro');

    var allSteps = this.root.querySelectorAll('[data-sf-step]');
    Array.prototype.forEach.call(allSteps, function (el) {
      el.hidden = el.dataset.sfStep !== step;
    });

    this.updateProgress(step);

    var sharedBack = this.root.querySelector('[data-sf-back]');
    if (sharedBack) sharedBack.classList.toggle('sf-back--invisible', step === 'scope');

    if (step === 'scope') {
      this.setButtonEnabled('next-from-scope', !!this.state.scope);
    }
    if (step === 'room') {
      this.setButtonEnabled('next-from-room', !!this.state.room);
    }
    if (step === 'space') {
      this.setButtonEnabled('next-from-space', !!this.state.building);
    }
    if (step === 'ventilation') {
      this.setButtonEnabled('open-results', !!this.state.ventilation);
    }

    var activeStep = this.root.querySelector('[data-sf-step="' + step + '"]');
    if (activeStep) {
      var focusTarget = activeStep.querySelector('h3, .sf-title, input:not([type="hidden"])');
      if (focusTarget && !focusTarget.matches(':focus')) {
        setTimeout(function () {
          focusTarget.setAttribute('tabindex', '-1');
          focusTarget.focus({ preventScroll: false });
        }, 50);
      }
    }
  };

  SolutionFinder.prototype.updateProgress = function (step) {
    var flow = this.state.flow;
    var idx = flow.indexOf(step);
    if (idx < 0) return;
    var total = flow.length;
    var current = idx + 1;
    var pct = Math.round((current / total) * 100);

    var text = this.root.querySelector('[data-sf-progress-text]');
    var fill = this.root.querySelector('[data-sf-progress-fill]');
    var bar = this.root.querySelector('[data-sf-progress]');

    if (text) text.textContent = this.format(this.strings.step_of, { current: current, total: total });
    if (fill) fill.style.width = pct + '%';
    if (bar) bar.setAttribute('aria-valuenow', String(current));
  };

  SolutionFinder.prototype.openModal = function (step) {
    if (!this.modal) return;
    this.renderResults();
    this.openModalStep(step || 'results');
    if (this.container) this.container.hidden = true;
    this.modal.hidden = false;
    if (this.mode !== 'modal') {
      document.body.style.overflow = 'hidden';
    }
    this.track('sf_modal_open', { step: step });

    var self = this;
    setTimeout(function () {
      var focusable = self.modal.querySelector('h3, button, [href], input');
      if (focusable) focusable.focus({ preventScroll: true });
    }, 50);
  };

  SolutionFinder.prototype.closeModal = function () {
    if (!this.modal) return;
    this.modal.hidden = true;
    if (this.container) this.container.hidden = false;
    if (this.mode !== 'modal') {
      document.body.style.overflow = '';
    }
    this.track('sf_modal_close');
  };

  SolutionFinder.prototype.openModalStep = function (step) {
    var steps = this.modal.querySelectorAll('[data-sf-modal-step]');
    Array.prototype.forEach.call(steps, function (el) {
      el.hidden = el.dataset.sfModalStep !== step;
    });
  };

  SolutionFinder.prototype.computeRecommendation = function () {
    var state = this.state;
    var rules = this.config.rules;

    for (var i = 0; i < rules.length; i++) {
      if (this.matchesRule(rules[i], state)) {
        var rule = rules[i];
        var rec = {
          ruleId: rule.id,
          resultType: rule.result_type || 'single',
          reasoningKey: rule.reasoning_key,
          noteKey: rule.note_key || null,
          related: (rule.related || []).map(function (key) {
            return { key: key, handle: this.config.products[key] };
          }.bind(this))
        };

        if (rec.resultType === 'single') {
          rec.primary = rule.primary;
          rec.primaryHandle = this.config.products[rule.primary];
        } else if (rec.resultType === 'multi') {
          var ratio = this.config.valve_ratio_m2 || 20;
          var threshold = this.config.valve_count_threshold || 3;
          var count = Math.max(1, Math.floor((state.size || 0) / ratio));
          rec.valveCount = count;
          rec.showCount = count >= threshold;
          rec.bedroomProduct = rule.products.bedroom;
          rec.bedroomHandle = this.config.products[rule.products.bedroom];
          rec.livingRoomProduct = rule.products.living_room;
          rec.livingRoomHandle = this.config.products[rule.products.living_room];
        }
        // 'escalate' needs nothing further — no product fields set.

        this.recommendation = rec;
        this.track('sf_recommendation', {
          rule: rule.id,
          result_type: rec.resultType,
          scope: state.scope,
          room: state.room,
          building: state.building,
          size: state.size,
          ventilation: state.ventilation
        });
        return;
      }
    }
  };

  SolutionFinder.prototype.matchesRule = function (rule, state) {
    var c = rule.conditions;
    if (!c) return true;
    if (c.scope && c.scope !== state.scope) return false;
    if (c.room && c.room !== state.room) return false;
    if (c.building && c.building !== state.building) return false;
    if (c.ventilation && c.ventilation !== state.ventilation) return false;
    if (c.size_min != null && (state.size == null || state.size < c.size_min)) return false;
    if (c.size_max != null && (state.size == null || state.size > c.size_max)) return false;
    return true;
  };

  SolutionFinder.prototype.resolveString = function (value, building) {
    if (value && typeof value === 'object') {
      return value[building] || value['default'] || '';
    }
    return value || '';
  };

  SolutionFinder.prototype.getTemplates = function () {
    var products = {};
    var tpls = this.root.querySelectorAll('[data-sf-product-template]');
    Array.prototype.forEach.call(tpls, function (tpl) {
      products[tpl.dataset.sfProductTemplate] = tpl;
    });
    return products;
  };

  SolutionFinder.prototype.cloneCard = function (tpl, variant) {
    if (!tpl || !tpl.content) return null;
    var clone = tpl.content.cloneNode(true);
    var card = clone.querySelector('.sf-product-card');
    if (card && variant) {
      card.classList.remove('sf-product-card--primary');
      card.classList.add('sf-product-card--' + variant);
    }
    return clone;
  };

  SolutionFinder.prototype.renderResults = function () {
    var rec = this.recommendation;
    if (!rec) return;

    var products = this.getTemplates();
    var primaryHost = this.modal.querySelector('[data-sf-primary-product]');
    var matchTitle = this.modal.querySelector('[data-sf-match-title]');
    var reasoning = this.modal.querySelector('[data-sf-reasoning]');
    var relatedHost = this.modal.querySelector('[data-sf-related-grid]');
    var relatedContainer = this.modal.querySelector('[data-sf-related]');

    if (primaryHost) primaryHost.innerHTML = '';
    if (relatedHost) relatedHost.innerHTML = '';

    if (rec.resultType === 'single') {
      var primaryTpl = products[rec.primaryHandle];
      if (primaryHost && primaryTpl) {
        var clone = this.cloneCard(primaryTpl, 'primary');
        if (clone) primaryHost.appendChild(clone);
      }

      var primaryName = this.getProductName(primaryTpl);
      if (matchTitle) {
        var matchTitleTemplate = this.resolveString(this.strings.match_title, this.state.building);
        matchTitle.textContent = this.format(matchTitleTemplate, { product_name: primaryName });
      }
      if (reasoning) {
        var reasoningStrings = this.strings.reasoning;
        var template = this.resolveString(
          reasoningStrings[rec.reasoningKey] || reasoningStrings['default'],
          this.state.building
        );
        var text = this.format(template, { size: this.state.size || '' });
        if (rec.noteKey && this.strings.notes && this.strings.notes[rec.noteKey]) {
          text += ' ' + this.strings.notes[rec.noteKey];
        }
        reasoning.innerHTML = text;
      }
      if (primaryHost) primaryHost.hidden = false;

    } else if (rec.resultType === 'multi') {
      var bedroomTpl = products[rec.bedroomHandle];
      var livingTpl = products[rec.livingRoomHandle];
      var bedroomName = this.getProductName(bedroomTpl);
      var livingName = this.getProductName(livingTpl);

      if (primaryHost) {
        var b = this.cloneCard(bedroomTpl, 'related');
        var l = this.cloneCard(livingTpl, 'related');
        var wrap = document.createElement('div');
        wrap.className = 'sf-related-grid';
        if (b) wrap.appendChild(b);
        if (l) wrap.appendChild(l);
        primaryHost.appendChild(wrap);
        primaryHost.hidden = false;
      }

      if (matchTitle) {
        matchTitle.textContent = this.strings.multi_result_title || '';
      }
      if (reasoning) {
        var msgTemplate = rec.showCount ? this.strings.multi_result_high : this.strings.multi_result_low;
        reasoning.textContent = this.format(msgTemplate, {
          count: rec.valveCount,
          bedroom_product: bedroomName,
          living_room_product: livingName
        });
      }

    } else if (rec.resultType === 'escalate') {
      if (primaryHost) primaryHost.hidden = true;
      if (matchTitle) matchTitle.textContent = this.strings.escalate_title || '';
      if (reasoning) {
        var reasoningStrings2 = this.strings.reasoning;
        var escTemplate = reasoningStrings2[rec.reasoningKey] || this.strings.escalate_reasoning || '';
        reasoning.textContent = this.format(escTemplate, {});
      }
    }

    // Related grid (works the same for all result types)
    if (relatedHost && relatedContainer) {
      var hasRelated = false;
      rec.related.forEach(function (r) {
        var tpl = products[r.handle];
        var clone = this.cloneCard(tpl, 'related');
        if (!clone) return;
        var atcForm = clone.querySelector('[data-sf-atc-form]');
        if (atcForm) atcForm.remove();
        var actions = clone.querySelector('.sf-product-card-actions');
        if (actions) actions.remove();
        relatedHost.appendChild(clone);
        hasRelated = true;
      }.bind(this));
      relatedContainer.hidden = !hasRelated;
    }
  };

  SolutionFinder.prototype.getProductName = function (tpl) {
    if (!tpl || !tpl.content) return '';
    var titleEl = tpl.content.querySelector('.sf-product-card-title');
    return titleEl ? titleEl.textContent.trim() : '';
  };

  SolutionFinder.prototype.buildRecommendedProductValue = function () {
    var rec = this.recommendation;
    if (!rec) return '';
    if (rec.resultType === 'single') return rec.primaryHandle || '';
    if (rec.resultType === 'multi') {
      return [rec.bedroomHandle, rec.livingRoomHandle].filter(Boolean).join(',');
    }
    if (rec.resultType === 'escalate') return 'needs_consultation';
    return '';
  };

  SolutionFinder.prototype.prepareForm = function () {
    var self = this;
    var set = function (sel, val) {
      var el = self.modal.querySelector(sel);
      if (el) el.value = val;
    };
    set('[data-sf-hidden-scope]', this.state.scope);
    set('[data-sf-hidden-room]', this.state.room);
    set('[data-sf-hidden-building]', this.state.building);
    set('[data-sf-hidden-size]', this.state.size || '');
    set('[data-sf-hidden-ventilation]', this.state.ventilation);
    set('[data-sf-hidden-product]', this.buildRecommendedProductValue());
    set('[data-sf-hidden-result-type]', this.recommendation ? this.recommendation.resultType : '');

    var summaryEl = this.modal.querySelector('[data-sf-form-summary-text]');
    if (summaryEl) {
      var summaryText;
      if (this.state.scope === 'room') {
        var roomLabel = this.strings.room_labels[this.state.room] || this.state.room;
        summaryText = this.format(this.strings.summary_template_room, { room: roomLabel });
      } else {
        var buildingLabel = this.strings.building_labels[this.state.building] || this.state.building;
        summaryText = this.format(this.strings.summary_template, {
          building: buildingLabel,
          size: this.state.size || '?'
        });
      }
      summaryEl.textContent = summaryText;
    }
  };

  SolutionFinder.prototype.handleFormSubmit = function (e, form) {
    this.clearFormErrors();

    if (form.querySelector('input[name="website"]').value) {
      e.preventDefault();
      this.track('sf_form_honeypot_triggered');
      this.openModalStep('success');
      return;
    }

    var valid = true;
    var nameEl = form.querySelector('input[name="name"]');
    var contactEl = form.querySelector('input[name="contact_info"]');

    if (!nameEl.value.trim()) {
      this.setFieldError('name', this.strings.required_field);
      valid = false;
    }

    var contactInfo = contactEl.value.trim();
    if (!contactInfo) {
      this.setFieldError('contact_info', this.strings.required_field);
      valid = false;
    } else if (contactInfo.indexOf('@') !== -1 && !this.isValidEmail(contactInfo)) {
      this.setFieldError('contact_info', this.strings.invalid_email);
      valid = false;
    }

    if (!valid) {
      e.preventDefault();
      this.track('sf_form_validation_failed');
      return;
    }

    // Valid — do NOT preventDefault. The form submits natively into the
    // hidden iframe (target="sf-contact-target"), which is the reliable
    // path (fetch()-based submission to /contact returns a 400; a real
    // native submission does not). Success is detected via the iframe's
    // load event, wired up in bindEvents.
    var submitBtn = form.querySelector('[data-sf-submit]');
    submitBtn.disabled = true;
    submitBtn.textContent = this.strings.submitting;
    this.awaitingSubmit = true;

    var data = {
      name:                nameEl.value.trim(),
      contact_info:        contactInfo,
      message:             form.querySelector('[name="message"]').value.trim(),
      scope:               form.querySelector('[name="scope"]').value,
      room:                form.querySelector('[name="room"]').value,
      building:            form.querySelector('[name="building"]').value,
      size:                form.querySelector('[name="size"]').value,
      ventilation:         form.querySelector('[name="ventilation"]').value,
      recommended_product: form.querySelector('[name="recommended_product"]').value,
      result_type:         form.querySelector('[name="result_type"]').value,
      locale:              form.querySelector('[name="locale"]').value
    };

    // Fill in Shopify's expected contact[] fields before the native submit fires.
    var shopifyName = form.querySelector('[data-sf-shopify-name]');
    var shopifyEmail = form.querySelector('[data-sf-shopify-email]');
    var shopifyBody = form.querySelector('[data-sf-shopify-body]');
    if (shopifyName) shopifyName.value = data.name;
    if (shopifyEmail) shopifyEmail.value = contactInfo.indexOf('@') !== -1 ? contactInfo : '';
    if (shopifyBody) shopifyBody.value = this.buildEmailBody(data);

    // No preventDefault, no fetch — the browser handles the actual submit.
  };

  // Builds a readable plain-text summary for the store notification email,
  // since Shopify's default contact-form template doesn't automatically
  // render every custom hidden field.
  SolutionFinder.prototype.buildEmailBody = function (data) {
    var labels = this.strings.email_labels || {};
    var lines = [];

    lines.push((labels.scope || 'Scope') + ': ' + (data.scope === 'room' ? (labels.scope_room || 'Room') : (labels.scope_whole_home || 'Whole home')));

    if (data.scope === 'room') {
      var roomLabel = this.strings.room_labels[data.room] || data.room;
      lines.push((labels.room || 'Room') + ': ' + roomLabel);
    } else {
      var buildingLabel = this.strings.building_labels[data.building] || data.building;
      lines.push((labels.building || 'Building type') + ': ' + buildingLabel);
      lines.push((labels.size || 'Size') + ': ' + data.size + ' m²');
    }

    lines.push((labels.ventilation || 'Ventilation') + ': ' + data.ventilation);
    lines.push((labels.recommendation || 'Recommendation') + ': ' + (data.recommended_product || '—'));
    lines.push((labels.result_type || 'Result type') + ': ' + data.result_type);

    if (contactInfoLooksLikePhone(data.contact_info)) {
      lines.push((labels.phone || 'Phone') + ': ' + data.contact_info);
    }

    if (data.message) {
      lines.push('');
      lines.push((labels.customer_message || 'Customer message') + ':');
      lines.push(data.message);
    }

    return lines.join('\n');

    function contactInfoLooksLikePhone(value) {
      return value.indexOf('@') === -1;
    }
  };

  SolutionFinder.prototype.setFieldError = function (fieldName, message) {
    var errEl = this.modal.querySelector('[data-sf-error-for="' + fieldName + '"]');
    if (errEl) errEl.textContent = message;
    var input = this.modal.querySelector('[name="' + fieldName + '"]');
    if (input && input.closest('.sf-field')) {
      input.closest('.sf-field').classList.add('sf-field--error');
    }
  };

  SolutionFinder.prototype.clearFormErrors = function () {
    var errs = this.modal.querySelectorAll('[data-sf-error-for]');
    Array.prototype.forEach.call(errs, function (e) { e.textContent = ''; });
    var fields = this.modal.querySelectorAll('.sf-field--error');
    Array.prototype.forEach.call(fields, function (f) { f.classList.remove('sf-field--error'); });
    var formErr = this.modal.querySelector('[data-sf-form-error]');
    if (formErr) { formErr.textContent = ''; formErr.hidden = true; }
  };

  SolutionFinder.prototype.showFormError = function (message) {
    var formErr = this.modal.querySelector('[data-sf-form-error]');
    if (formErr) { formErr.textContent = message; formErr.hidden = false; }
  };

  SolutionFinder.prototype.isValidEmail = function (str) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str);
  };

  SolutionFinder.prototype.restart = function () {
    this.closeModal();
    this.state = {
      step: 'intro',
      history: [],
      flow: FLOW_WHOLE_HOME,
      scope: '',
      room: '',
      building: '',
      size: null,
      ventilation: ''
    };
    this.recommendation = null;

    var form = this.modal.querySelector('[data-sf-modal-step="form"] form');
    if (form) form.reset();
    this.awaitingSubmit = false;
    var submitBtn = this.modal.querySelector('[data-sf-submit]');
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.textContent = this.strings.submit;
    }
    this.clearFormErrors();
    this.openModalStep('results');

    var inputs = this.root.querySelectorAll('[data-sf-input]');
    Array.prototype.forEach.call(inputs, function (i) {
      if (i.type === 'checkbox' || i.type === 'radio') i.checked = false;
    });
    this.setButtonEnabled('next-from-space', false);

    this.renderStep('intro');
    this.track('sf_restart');
  };

  SolutionFinder.prototype.format = function (template, vars) {
    if (!template) return '';
    return template.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, key) {
      return vars[key] != null ? String(vars[key]) : '';
    });
  };

  SolutionFinder.prototype.track = function (eventName, data) {
    data = data || {};
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, Object.assign({ component: 'solution_finder' }, data));
      }
      if (typeof window.dataLayer !== 'undefined' && window.dataLayer.push) {
        window.dataLayer.push(Object.assign({ event: eventName, component: 'solution_finder' }, data));
      }
    } catch (e) {
      console.warn('[SolutionFinder] Tracking error', e);
    }
  };

  function init() {
    var roots = document.querySelectorAll('[data-sf-root]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.sfInitialized) return;
      root.dataset.sfInitialized = 'true';
      new SolutionFinder(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.MobairSolutionFinder = { init: init };
})();